<template>
  <div class="pp-am">
    <div class="hdr">
      <span class="label">{{ t('property.accountManagerSidebar.label') }}</span>
      <span class="dic-badge">
        <span class="d">D</span>
        Insiders
      </span>
    </div>
    <div class="row">
      <AccountManagerAvatar
        :src="manager?.photoUrl"
        :first-name="manager?.firstName ?? (manager ? undefined : 'Insiders')"
        :last-name="manager?.lastName ?? (manager ? undefined : t('property.accountManagerSidebar.teamLastName'))"
        size="md"
      />
      <div class="who">
        <div class="n">
          {{ displayName }}
        </div>
        <div class="s">
          <span class="dot"></span>
          {{
            manager
              ? t('property.accountManagerSidebar.onlineNow')
              : t('property.accountManagerSidebar.availableOnWhatsapp')
          }}
        </div>
      </div>
    </div>
    <div
      v-if="manager?.languages?.length"
      class="langs"
    >
      <span
        v-for="lang in manager.languages"
        :key="lang"
        class="l"
        >{{ lang }}</span
      >
    </div>
    <a
      :href="whatsappLink"
      target="_blank"
      rel="noopener noreferrer"
      class="wa"
    >
      <UIIcon
        name="whatsapp"
        size="sm"
      />
      {{ t('property.accountManagerSidebar.messageOnWhatsapp') }}
    </a>
  </div>
</template>

<script setup lang="ts">
import type { AccountManagerCard } from '@/api/queries/accountManagers';

const props = defineProps<{
  manager: AccountManagerCard | null | undefined;
}>();

const { t } = useI18n();

// Same default-WhatsApp the floaty support bubble uses (components/support/
// OpenAgentInfo.vue). When the agent has no platformAccountManager assigned —
// or the public-fallback row is missing in Prisma — we still want a working
// "Message on WhatsApp" CTA instead of a dead placeholder card.
const DEFAULT_DIC_WHATSAPP = '971527033506';

const displayName = computed(() => {
  if (!props.manager) return t('property.accountManagerSidebar.fallbackName');
  const first = props.manager.firstName ?? '';
  const last = props.manager.lastName ?? '';
  return `${first} ${last}`.trim() || t('property.accountManagerSidebar.fallbackName');
});

const whatsappLink = computed(() => {
  const phone = props.manager?.phone;
  const digits = phone ? phone.replace(/\D/g, '') : DEFAULT_DIC_WHATSAPP;
  return `https://wa.me/${digits}`;
});
</script>

<style scoped lang="scss">
.pp-am {
  background: #f8f7f3;
  border-radius: 16px;
  padding: 18px;

  .hdr {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);

    .label {
      font-family: 'JetBrains Mono', ui-monospace, monospace;
      font-size: 10px;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: rgba(13, 15, 21, 0.62);
      font-weight: 700;
    }

    .dic-badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding-block: 3px;
      padding-inline: 4px 9px;
      border-radius: 999px;
      background: #0d0f15;
      color: #ffb648;
      font-size: 11px;
      font-weight: 700;

      .d {
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background: #ffb648;
        color: #0d0f15;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-family: 'Syne', 'Manrope', sans-serif;
        font-size: 9px;
        font-weight: 800;
      }
    }
  }

  .row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;

    .who {
      min-width: 0;

      .n {
        font-size: 14px;
        font-weight: 700;
        color: #0d0f15;
      }

      .s {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-top: 2px;
        font-size: 12px;
        color: #126b2f;
        font-weight: 500;

        .dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #22c55e;
        }
      }
    }
  }

  .langs {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    margin-bottom: 14px;

    .l {
      padding: 3px 8px;
      border-radius: 6px;
      background: #0d0f15;
      color: white;
      font-size: 11px;
      font-weight: 600;
    }
  }

  .wa {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    height: 44px;
    border-radius: 10px;
    background: #25d366;
    color: white;
    font-size: 13px;
    font-weight: 700;
    transition: background 0.15s ease;

    &:hover {
      background: #1fb655;
    }
  }
}
</style>
